import { useDebounce } from "src/hooks/useDebounce";
import { Recipe } from "../cards/types";
import { useMemo } from "react";
import { NarrativePrefixType } from "pages/api/settings/narrative-prefix/types";
import { parseJson } from "../utils/json";

interface PromptStructure {
  role: any[];
  input_instructions: any[];
  rules: any[];
  output_instructions: any[];
  tone_options: string[];
}

export function useComposedPrompt(
  recipe: Recipe | Recipe[],
  prompt?: string,
  prefixes?: NarrativePrefixType[],
  debounceMs: number = 1000
) {
  const rawPrompt = useMemo(() => {
    return buildPrompt(recipe, prompt, prefixes);
  }, [recipe, prompt, prefixes]);
  return useDebounce(rawPrompt, debounceMs);
}

function buildPrompt(
  recipe: Recipe | Recipe[],
  prompt?: string,
  prefixes?: NarrativePrefixType[]
) {
  const structure: PromptStructure = {
    role: [],
    input_instructions: [],
    rules: [],
    output_instructions: [],
    tone_options: [],
  };

  // Handle prefixes
  prefixes?.forEach((prefix) => {
    try {
      const { prompt_type, prompt: prefixPrompt } = parseJson(prefix.prefix);
      if (!prompt_type || !prefixPrompt) return;
      switch (prompt_type) {
        case "role": {
          structure.role.push(prefixPrompt);
          break;
        }
        case "input_instructions": {
          structure.input_instructions.push(prefixPrompt);
          break;
        }
        case "rule": {
          structure.rules.push(prefixPrompt);
          break;
        }
        case "output_instructions": {
          structure.output_instructions.push(prefixPrompt);
          break;
        }
        case "tone": {
          if (Array.isArray(prefixPrompt)) {
            structure.tone_options = [
              ...new Set([...structure.tone_options, ...prefixPrompt]),
            ];
          }
          break;
        }
      }
    } catch (error) {
      console.error("Failed to parse prefix:", prefix, error);
    }
  });

  // Deduplicate recipes by id
  const recipesArr: Recipe[] = Array.isArray(recipe)
    ? recipe.reduce<Recipe[]>((acc, r) => {
        if (r?.id && !acc.some((item) => item.id === r.id)) acc.push(r);
        return acc;
      }, [])
    : recipe
    ? [recipe]
    : [];

  // Process each unique recipe
  recipesArr.forEach((rec) => {
    if (!rec?.prefix) return;
    try {
      const recipeData = parseJson(rec.prefix);
      Object.entries(recipeData).forEach(([key, value]) => {
        switch (key) {
          case "role": {
            structure.role.push(value);
            break;
          }
          case "input_instructions": {
            structure.input_instructions.push(value);
            break;
          }
          case "rule": {
            structure.rules.push(value);
            break;
          }
          case "output_instructions": {
            structure.output_instructions.push(value);
            break;
          }
          case "tone": {
            if (Array.isArray(value)) {
              structure.tone_options = [
                ...new Set([...structure.tone_options, ...value]),
              ];
            }
            break;
          }
        }
      });
    } catch (error) {
      console.error(
        "Recipe prefix is not JSON, using as raw text",
        error,
        rec.prefix
      );
    }
  });

  // Build the final prompt string
  const promptSections = [
    structure.role.length > 0 &&
      `"roles":\n${JSON.stringify(structure.role, null, 2)}`,
    structure.input_instructions.length > 0 &&
      `"input_instructions":\n${JSON.stringify(
        structure.input_instructions,
        null,
        2
      )}`,
    structure.rules.length > 0 &&
      `"rules":\n${JSON.stringify(structure.rules, null, 2)}`,
    structure.tone_options.length > 0 &&
      `"tone_options":\n${JSON.stringify(structure.tone_options, null, 2)}`,
    structure.output_instructions.length > 0 &&
      `"output_instructions":\n${JSON.stringify(
        structure.output_instructions,
        null,
        2
      )}`,
    prompt && `"additional_instructions": ${JSON.stringify(prompt)}`,
  ].filter(Boolean);

  return `{
${promptSections.join(",\n\n")}
}`;
}
